import { CheckboxField } from '@ovComponents/2-component/checkboxField/checkboxField';
import {
  ConfirmationModal,
  type ConfirmationModalProps,
} from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { Box, Typography } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const useConfirmationDialog = () => {
  type ConfirmationResult = {
    confirmed: boolean;
    closeDialog: () => void;
  };
  const [resolver, setResolver] = useState<
    ((result: ConfirmationResult) => void) | null
  >(null);
  const hasPending = !!resolver;

  /** Modal state */
  const [title, setTitle] = useState<string>('');
  const [bodyText, setBodyText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [autoConfirmKey, setAutoConfirmKey] = useState<string | undefined>(
    undefined,
  );
  const [confirmButtonColor, setConfirmButtonColor] =
    useState<ConfirmationModalProps['confirmButtonColor']>('primary');
  /** ************* */

  const { t } = useTranslation('workflowCompletions');

  const getUserConfirmation = (args: {
    title: string;
    bodyText: string;
    autoConfirmKey?: string;
    setConfirmButtonColor?: ConfirmationModalProps['confirmButtonColor'];
  }) =>
    new Promise<ConfirmationResult>((resolve) => {
      const serializedAutoConfirmKey =
        args.autoConfirmKey && `autoConfirm-${args.autoConfirmKey}`;
      if (
        serializedAutoConfirmKey &&
        localStorage.getItem(serializedAutoConfirmKey) === 'true'
      ) {
        resolve({
          confirmed: true,
          closeDialog,
        });
        return;
      }
      setAutoConfirmKey(serializedAutoConfirmKey);
      setTitle(args.title);
      setBodyText(args.bodyText);
      setResolver(() => resolve);
      setConfirmButtonColor(args.setConfirmButtonColor || 'primary');
    });

  const closeDialog = () => {
    setLoading(false);
    setAutoConfirmKey(undefined);
    setResolver(null);
  };

  const onConfirm = () => {
    setLoading(true);
    resolver?.({
      confirmed: true,
      closeDialog,
    });
  };

  const onCancel = () => {
    setLoading(true);
    resolver?.({
      confirmed: false,
      closeDialog,
    });
  };

  return {
    getUserConfirmation,
    confirmationDialog: (
      <ConfirmationModal
        confirmButtonColor={confirmButtonColor}
        open={hasPending}
        onConfirm={onConfirm}
        onCancel={onCancel}
        title={title}
        confirmButtonLabel={t('shared:confirm')}
        loading={loading}
      >
        <Typography variant='bodyLarge'>{bodyText}</Typography>
        {autoConfirmKey && (
          <Box sx={{ mt: 2 }}>
            <CheckboxField
              size='medium'
              label={t('createAccountForGoal.confirmDialog.dontShowAgain')}
              checked={localStorage.getItem(autoConfirmKey) === 'true'}
              onChange={(checked) => {
                localStorage.setItem(
                  autoConfirmKey,
                  checked ? 'true' : 'false',
                );
              }}
            />
          </Box>
        )}
      </ConfirmationModal>
    ),
  };
};
